import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { debounce } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import invoiceLogo from "../../../../../assets/images/invoicing-icon.svg";
import CommercialAirlineNavigationBar from "../../../../../components/element/CommercialAirlineNavigationBar";
import ErrorPopup from "../../../../../components/element/ErrorPopup";
import Loader from "../../../../../components/element/Loader";
import useAirlineAdminApi from "../../../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../../../services/StorageProvider";
import {
  COMMERCIAL_INVOICE_STATUS,
  INVOICE_BREADCRUMB_SCREEN_NAME,
  PAGINATION_PAGE_SIZE,
} from "../../../../../utils/constants";
import ROUTE_PATHS from "../../../../../utils/routePaths";
import { getAirportData } from "../../../../../utils/utils";
import InvoiceFlightSearch from "../../Views/InvoiceFlightSearch";
import FeeInvoiceTable from "./FeeInvoiceTable";

const FeeInvoiceDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchCommercialFlightPlansFeeInvoice } = useAirlineAdminApi();
  const { airportData } = useContext(StorageContext);
  const [currentPage, setCurrentPage] = useState(() => {
    // Always start with page 0 when the component mounts
    return 0;
  });
  const pageSize = PAGINATION_PAGE_SIZE;

  const getStateData = () => {
    if (location.state) {
      sessionStorage.setItem(
        "feeInvoiceDetailData",
        JSON.stringify(location.state)
      );
      return location.state;
    }
    const storedData = sessionStorage.getItem("feeInvoiceDetailData");
    return storedData ? JSON.parse(storedData) : null;
  };

  const stateData = getStateData();
  const invoiceData = stateData?.invoiceData;
  const selectedAirlineId = stateData?.selectedAirlineId;
  const invoiceStatus = stateData?.invoiceStatus;
  const lineItem = stateData?.lineItem;
  const feeType = stateData?.feeType;
  const [isSearching, setIsSearching] = useState(false);
  const [searchFlightNumber, setSearchFlightNumber] = useState("");
  const [flightPlansData, setFlightPlansData] = useState(null);
  const invoiceId = stateData?.id;

  const fetchCommercialFlightPlansFeeInvoiceMutation = useMutation({
    mutationKey: [
      "fetchCommercialFlightPlansFeeInvoice",
      currentPage,
      pageSize,
      searchFlightNumber,
    ],
    mutationFn: () =>
      fetchCommercialFlightPlansFeeInvoice(lineItem?.id, {
        ...(searchFlightNumber && { flightNumber: searchFlightNumber }),
        page: currentPage,
        size: pageSize,
      }),
    onSuccess: (data) => {
      setFlightPlansData(data);
    },
  });
  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const getBreadcrumbScreenName = (invoiceStatus) => {
    if (!invoiceStatus) return "";

    const statusArray = invoiceStatus.split(',');
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.PAID_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.PAID_INVOICE;
    }
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.SETTLED_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.SETTLED_INVOICE;
    }
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.OVERDUE_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.OVERDUE_INVOICE;
    }

    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.PARTIAL_PAYMENT)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.NEW_INVOICE;
    }

    return INVOICE_BREADCRUMB_SCREEN_NAME[invoiceStatus] || "";
  };

  const invoiceBreadcrumbScreenName = getBreadcrumbScreenName(invoiceStatus);

  useEffect(() => {
    fetchCommercialFlightPlansFeeInvoiceMutation.mutate();
    sessionStorage.setItem("feeInvoiceDetailCurrentPage", currentPage);
  }, [invoiceId, currentPage, pageSize, searchFlightNumber]);

  // Clear sessionStorage when component mounts to ensure we start with page 0
  useEffect(() => {
    sessionStorage.removeItem("feeInvoiceDetailCurrentPage");
  }, []);

  const debouncedSearchHandler = useCallback(
    debounce((flightNum) => {
      setSearchFlightNumber(flightNum);
      setCurrentPage(0);
    }, 500),
    []
  );

  const handleSearchClick = (flightNum) => {
    setIsSearching(flightNum);
    debouncedSearchHandler(flightNum);
  };

  const handleBackButton = () => {
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_DETAILS, {
      state: {
        invoiceData: stateData?.invoiceData,
        selectedAirlineId: stateData?.selectedAirlineId,
        id: stateData?.id,
        invoiceStatus: stateData?.invoiceStatus,
        lineItem: lineItem,
      },
    });
  };

  const renderNavigationBar = () => (
    <CommercialAirlineNavigationBar
      title={`Invoices ${invoiceData?.invoiceNumber || ""} - ${getAirportData(airportData, lineItem?.airportId)?.name || ""
        }`}
      navigationLogo={invoiceLogo}
      fullWidthContent={true}
      backContent={
        <div className="navigation-header">
          <Link
            to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
            className="navigation-header-link"
          >
            Home/
          </Link>
          <Link
            to={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE}
            className="navigation-header-link"
            onClick={() => sessionStorage.removeItem("invoiceDetailData")}
          >
            Invoices/{invoiceBreadcrumbScreenName}/
          </Link>
          <Link
            to={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_DETAILS}
            className="navigation-header-link"
            onClick={handleBackButton}
          >
            {invoiceData?.dynamicsInvoiceNumber || ""}
          </Link>
          /Details
        </div>
      }
    />
  );

  const renderContent = () => {
    if (fetchCommercialFlightPlansFeeInvoiceMutation.isPending)
      return <Loader />;
    if (fetchCommercialFlightPlansFeeInvoiceMutation.error)
      return (
        <ErrorPopup>
          {fetchCommercialFlightPlansFeeInvoiceMutation.error.message}
        </ErrorPopup>
      );
    return (
      <FeeInvoiceTable
        feeInvoiceDetails={flightPlansData?.content || []}
        feeType={feeType}
      />
    );
  };

  if (!invoiceData || !selectedAirlineId || !invoiceStatus) {
    return <Navigate to={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE} replace />;
  }

  return (
    <div>
      {renderNavigationBar()}

      <div className="invoice-detail">
        <InvoiceFlightSearch setIsSearching={handleSearchClick} />
        {renderContent()}
      </div>

      {flightPlansData && (
        <div className="pagination-container">
          <div className="pagination-content">
            Page {flightPlansData.number + 1} of {flightPlansData.totalPages}
          </div>
          <button
            className="pagination-button"
            onClick={handlePreviousPage}
            disabled={flightPlansData.first}
          >
            <CaretLeftOutlined />
          </button>
          <button
            className="pagination-button"
            onClick={handleNextPage}
            disabled={flightPlansData.last}
          >
            <CaretRightOutlined />
          </button>
        </div>
      )}
    </div>
  );
};

export default FeeInvoiceDetails;
